import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useSpring } from 'framer-motion';
import { FaBriefcase, FaGraduationCap } from 'react-icons/fa';
import Reveal from './ui/Reveal';
import { useI18n } from '../context/LanguageContext';

const Section = styled.section`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 6rem 2rem;
  @media (max-width: 768px) { padding: 4.5rem 1.25rem; }
`;

const Head = styled.div`
  text-align: center; margin-bottom: 3.5rem;
  h2 { font-size: clamp(2rem, 4.5vw, 2.9rem); margin-bottom: 1rem; }
  p { color: var(--text-muted); max-width: 560px; margin: 0 auto; }
`;

const Line = styled.div`
  position: relative; max-width: 780px; margin: 0 auto; padding-left: 2.5rem;
  /* faint track — the bright line below draws itself over it on scroll */
  &::before {
    content: ''; position: absolute; left: 9px; top: 6px; bottom: 6px; width: 2px;
    background: var(--border-strong);
  }
  @media (max-width: 540px) { padding-left: 2rem; }
`;

const DrawnLine = styled(motion.div)`
  position: absolute; left: 9px; top: 6px; bottom: 6px; width: 2px;
  background: linear-gradient(180deg, var(--primary), var(--violet), var(--secondary));
  box-shadow: 0 0 12px rgba(168, 85, 247, 0.55);
  transform-origin: top;
`;

const Item = styled(motion.div)`
  position: relative; padding: 0 0 2.25rem 1.5rem;
  &:last-child { padding-bottom: 0; }
  &::before {
    content: ''; position: absolute; left: -2.5rem; top: 2px;
    width: 20px; height: 20px; border-radius: 50%;
    background: var(--bg); border: 2px solid var(--primary-light);
    box-shadow: 0 0 0 4px rgba(99,102,241,0.12);
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }
  &:hover::before { border-color: var(--secondary); box-shadow: 0 0 0 5px rgba(236,72,153,0.18); }
  @media (max-width: 540px) { &::before { left: -2rem; width: 16px; height: 16px; } }
`;

const Box = styled.div`
  border: 1px solid var(--border); background: var(--surface);
  border-radius: 18px; padding: 1.4rem 1.5rem;
  transition: border-color 0.3s ease, transform 0.3s ease;
  ${Item}:hover & { border-color: var(--border-strong); transform: translateX(4px); }
`;

const Top = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
  flex-wrap: wrap; margin-bottom: 0.3rem;
  .left { display: flex; align-items: center; gap: 0.75rem; }
  .icon {
    display: grid; place-items: center; width: 38px; height: 38px; border-radius: 11px;
    background: var(--bg-alt); border: 1px solid var(--border);
    color: ${(p) => (p.$edu ? 'var(--secondary)' : 'var(--primary-light)')};
    font-size: 1.05rem; flex-shrink: 0;
  }
  h3 { font-size: 1.1rem; }
  .date {
    font-size: 0.78rem; font-weight: 600; color: var(--primary-light);
    background: var(--bg-alt); border: 1px solid var(--border);
    padding: 0.3rem 0.7rem; border-radius: 999px; white-space: nowrap;
  }
`;

const Org = styled.p`
  color: var(--secondary); font-weight: 600; font-size: 0.92rem;
  margin: 0 0 0.7rem 3.25rem;
  @media (max-width: 540px) { margin-left: 0; }
`;

const Points = styled.ul`
  margin: 0 0 0 3.25rem; padding: 0; list-style: none;
  li {
    position: relative; padding-left: 1.1rem; margin-bottom: 0.4rem;
    color: var(--text-muted); font-size: 0.94rem; line-height: 1.6;
  }
  li::before {
    content: ''; position: absolute; left: 0; top: 0.6rem;
    width: 5px; height: 5px; border-radius: 50%; background: var(--primary-light);
  }
  @media (max-width: 540px) { margin-left: 0; text-align: left; }
`;

const Experience = () => {
  const { t, data } = useI18n();
  const { timeline } = data;
  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: lineRef,
    offset: ['start 0.85', 'end 0.55'],
  });
  const drawn = useSpring(scrollYProgress, { stiffness: 90, damping: 26 });

  return (
  <Section id="experience">
    <Reveal>
      <Head>
        <span className="eyebrow">{t.experience.eyebrow}</span>
        <h2>{t.experience.h2a}<span className="gradient-text">{t.experience.h2b}</span></h2>
        <p>{t.experience.sub}</p>
      </Head>
    </Reveal>

    <Line ref={lineRef}>
      <DrawnLine style={{ scaleY: drawn }} />
      {timeline.map((item, i) => {
        const edu = item.type === 'education';
        return (
          <Item
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: i * 0.05, ease: 'easeOut' }}
          >
            <Box>
              <Top $edu={edu}>
                <div className="left">
                  <span className="icon">{edu ? <FaGraduationCap /> : <FaBriefcase />}</span>
                  <h3>{item.title}</h3>
                </div>
                <span className="date">{item.date}</span>
              </Top>
              <Org>{item.org}</Org>
              <Points>
                {item.points.map((pt, idx) => <li key={idx}>{pt}</li>)}
              </Points>
            </Box>
          </Item>
        );
      })}
    </Line>
  </Section>
  );
};

export default Experience;
